import { useState, useEffect, useCallback } from "react";
import { useAuth } from "contexts/AuthContext";
import PropTypes from "prop-types";
import Box from "@mui/material/Box";
import Typography from "@mui/material/Typography";
import Button from "@mui/material/Button";
import Stack from "@mui/material/Stack";
import EditIcon from "@mui/icons-material/EditOutlined";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import GroupIcon from "@mui/icons-material/GroupOutlined";
import HowToRegIcon from "@mui/icons-material/HowToRegOutlined";
import Swal from "sweetalert2";
import StatusChip from "components/StatusChip";
import { API_BASE } from "config/api";

const SerialCell = ({ value }) => (
  <Typography variant="caption" color="text.secondary" fontWeight={600}>
    {value}
  </Typography>
);
SerialCell.propTypes = { value: PropTypes.any.isRequired };

const GroupCell = ({ row }) => (
  <Box textAlign="left">
    <Typography variant="button" fontWeight={600} display="block">
      {row.original.name}
    </Typography>
    <Typography variant="caption" color="text.secondary">
      {row.original.meetingDay
        ? `${row.original.meetingDay} ${row.original.meetingTime || ""}`
        : "No schedule set"}
    </Typography>
  </Box>
);
GroupCell.propTypes = { row: PropTypes.shape({ original: PropTypes.object }).isRequired };

const LeaderCell = ({ value }) => (
  <Typography variant="button" fontWeight={500}>
    {value || "—"}
  </Typography>
);
LeaderCell.propTypes = { value: PropTypes.string };
LeaderCell.defaultProps = { value: "" };

const StatusCell = ({ value }) => <StatusChip value={value} />;
StatusCell.propTypes = { value: PropTypes.string.isRequired };

const ActionsCell = ({ row, handleEdit, handleDelete, handleMembers, handleAttendance }) => (
  <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap">
    <Button
      size="small"
      variant="outlined"
      color="info"
      startIcon={<GroupIcon fontSize="small" />}
      onClick={() => handleMembers(row.original)}
    >
      Members ({row.original.memberCount})
    </Button>
    <Button
      size="small"
      variant="outlined"
      color="success"
      startIcon={<HowToRegIcon fontSize="small" />}
      onClick={() => handleAttendance(row.original)}
    >
      Attendance
    </Button>
    <Button
      size="small"
      variant="outlined"
      color="primary"
      startIcon={<EditIcon fontSize="small" />}
      onClick={() => handleEdit(row.original)}
    >
      Edit
    </Button>
    <Button
      size="small"
      variant="outlined"
      color="error"
      startIcon={<DeleteIcon fontSize="small" />}
      onClick={() => handleDelete(row.original)}
    >
      Delete
    </Button>
  </Stack>
);
ActionsCell.propTypes = {
  row: PropTypes.shape({ original: PropTypes.object }).isRequired,
  handleEdit: PropTypes.func.isRequired,
  handleDelete: PropTypes.func.isRequired,
  handleMembers: PropTypes.func.isRequired,
  handleAttendance: PropTypes.func.isRequired,
};

const emptyForm = () => ({
  id: null,
  name: "",
  description: "",
  leader_member_id: null,
  leaderLabel: "",
  meeting_day: "",
  meeting_time: "",
  location: "",
  status: "active",
});

export default function useSmallGroupData() {
  const { church } = useAuth();

  const [groupData, setGroupData] = useState({
    columns: [
      { Header: "SN", accessor: "serial", align: "center", width: "5%", Cell: SerialCell },
      { Header: "Group", accessor: "name", align: "left", width: "22%", Cell: GroupCell },
      { Header: "Leader", accessor: "leaderName", align: "left", width: "15%", Cell: LeaderCell },
      { Header: "Location", accessor: "location", align: "left", width: "15%" },
      { Header: "Status", accessor: "status", align: "center", width: "10%", Cell: StatusCell },
      {
        Header: "Actions",
        accessor: "actions",
        align: "center",
        width: "33%",
        Cell: (props) => (
          <ActionsCell
            {...props}
            handleEdit={handleEdit}
            handleDelete={handleDelete}
            handleMembers={handleMembers}
            handleAttendance={handleAttendance}
          />
        ),
      },
    ],
    rows: [],
    isLoading: true,
    error: null,
  });

  const [editModalOpen, setEditModalOpen] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [membersModalOpen, setMembersModalOpen] = useState(false);
  const [attendanceModalOpen, setAttendanceModalOpen] = useState(false);
  const [selectedGroup, setSelectedGroup] = useState(null);
  const [form, setForm] = useState(emptyForm());
  const [isSaving, setIsSaving] = useState(false);

  // Leader search (member picker)
  const [leaderSearch, setLeaderSearch] = useState("");
  const [leaderResults, setLeaderResults] = useState([]);

  // Members dialog state
  const [groupMembers, setGroupMembers] = useState([]);
  const [memberSearch, setMemberSearch] = useState("");
  const [memberResults, setMemberResults] = useState([]);
  const [isLoadingMembers, setIsLoadingMembers] = useState(false);

  // Attendance dialog state
  const [attendanceRows, setAttendanceRows] = useState([]);
  const [isLoadingAttendance, setIsLoadingAttendance] = useState(false);

  const authHeaders = () => {
    const token = localStorage.getItem("authToken");
    return token ? { Accept: "application/json", Authorization: `Bearer ${token}` } : null;
  };

  const fetchGroups = useCallback(async () => {
    if (!church?.id) return;
    setGroupData((prev) => ({ ...prev, isLoading: true }));
    try {
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(`${API_BASE}/small-groups`, { headers });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      const groups = result.data || [];

      setGroupData((prev) => ({
        ...prev,
        rows: groups.map((g, index) => ({
          id: g.id,
          serial: index + 1,
          name: g.name,
          leaderName: g.leader ? `${g.leader.first_name} ${g.leader.surname}` : "",
          meetingDay: g.meeting_day,
          meetingTime: g.meeting_time,
          location: g.location || "—",
          status: g.status,
          memberCount: g.members_count || 0,
          originalData: g,
        })),
        isLoading: false,
        error: null,
      }));
    } catch (error) {
      console.error("Fetch small groups failed:", error);
      setGroupData((prev) => ({ ...prev, isLoading: false, error: error.message }));
    }
  }, [church?.id]);

  useEffect(() => {
    fetchGroups();
  }, [fetchGroups]);

  const handleAdd = () => {
    setForm(emptyForm());
    setLeaderSearch("");
    setLeaderResults([]);
    setEditModalOpen(true);
  };

  const handleEdit = useCallback((group) => {
    const raw = group.originalData || {};
    setForm({
      id: group.id,
      name: raw.name || "",
      description: raw.description || "",
      leader_member_id: raw.leader_member_id || null,
      leaderLabel: raw.leader ? `${raw.leader.first_name} ${raw.leader.surname}` : "",
      meeting_day: raw.meeting_day || "",
      meeting_time: raw.meeting_time || "",
      location: raw.location || "",
      status: raw.status || "active",
    });
    setLeaderSearch(raw.leader ? `${raw.leader.first_name} ${raw.leader.surname}` : "");
    setLeaderResults([]);
    setEditModalOpen(true);
  }, []);

  const handleDelete = useCallback((group) => {
    setSelectedGroup(group);
    setDeleteModalOpen(true);
  }, []);

  const searchMembers = async (query, setResults) => {
    if (!church?.id || !query.trim()) {
      setResults([]);
      return;
    }
    try {
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(
        `${API_BASE}/members/${church.id}/search?query=${encodeURIComponent(query)}`,
        { headers }
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      setResults(result.data || []);
    } catch (error) {
      console.error("Member search failed:", error);
      setResults([]);
    }
  };

  useEffect(() => {
    if (!leaderSearch.trim() || leaderSearch === form.leaderLabel) {
      setLeaderResults([]);
      return;
    }
    const timer = setTimeout(() => searchMembers(leaderSearch, setLeaderResults), 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [leaderSearch]);

  const handleSelectLeader = (member) => {
    setForm((prev) => ({
      ...prev,
      leader_member_id: member.id,
      leaderLabel: `${member.first_name} ${member.surname}`,
    }));
    setLeaderSearch(`${member.first_name} ${member.surname}`);
    setLeaderResults([]);
  };

  const handleSubmit = async () => {
    try {
      if (!form.name.trim()) throw new Error("Group name is required.");
      setIsSaving(true);
      const headers = authHeaders();
      if (!headers) return;

      const url = form.id ? `${API_BASE}/small-groups/${form.id}` : `${API_BASE}/small-groups`;
      const method = form.id ? "PUT" : "POST";

      const payload = {
        name: form.name,
        description: form.description || null,
        leader_member_id: form.leader_member_id || null,
        meeting_day: form.meeting_day || null,
        meeting_time: form.meeting_time || null,
        location: form.location || null,
        status: form.status,
      };

      const response = await fetch(url, {
        method,
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || `HTTP error! status: ${response.status}`);
      }

      Swal.fire({
        title: "Success!",
        text: `Group ${form.id ? "updated" : "created"}`,
        icon: "success",
      });
      fetchGroups();
      setEditModalOpen(false);
    } catch (error) {
      console.error("Save small group failed:", error);
      Swal.fire({ title: "Error!", text: error.message, icon: "error" });
    } finally {
      setIsSaving(false);
    }
  };

  const handleDeleteConfirm = async () => {
    try {
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(`${API_BASE}/small-groups/${selectedGroup.id}`, {
        method: "DELETE",
        headers,
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

      Swal.fire({ title: "Success!", text: "Group deleted", icon: "success" });
      fetchGroups();
      setDeleteModalOpen(false);
    } catch (error) {
      console.error("Delete small group failed:", error);
      Swal.fire({ title: "Error!", text: error.message, icon: "error" });
    }
  };

  const fetchGroupMembers = useCallback(async (groupId) => {
    setIsLoadingMembers(true);
    try {
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(`${API_BASE}/small-groups/${groupId}/members`, { headers });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      setGroupMembers(result.data || []);
    } catch (error) {
      console.error("Fetch group members failed:", error);
    } finally {
      setIsLoadingMembers(false);
    }
  }, []);

  const handleMembers = useCallback(
    (group) => {
      setSelectedGroup(group);
      setMemberSearch("");
      setMemberResults([]);
      fetchGroupMembers(group.id);
      setMembersModalOpen(true);
    },
    [fetchGroupMembers]
  );

  useEffect(() => {
    if (!memberSearch.trim()) {
      setMemberResults([]);
      return;
    }
    const timer = setTimeout(() => searchMembers(memberSearch, setMemberResults), 300);
    return () => clearTimeout(timer);
    // eslint-disable-next-line
  }, [memberSearch]);

  const handleAddGroupMember = async (member) => {
    try {
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(`${API_BASE}/small-groups/${selectedGroup.id}/members`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ member_id: member.id }),
      });
      if (!response.ok) {
        const body = await response.json().catch(() => ({}));
        throw new Error(body.message || `HTTP error! status: ${response.status}`);
      }
      setMemberSearch("");
      setMemberResults([]);
      fetchGroupMembers(selectedGroup.id);
      fetchGroups();
    } catch (error) {
      Swal.fire({ title: "Error!", text: error.message, icon: "error" });
    }
  };

  const handleRemoveGroupMember = async (memberId) => {
    try {
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(
        `${API_BASE}/small-groups/${selectedGroup.id}/members/${memberId}`,
        { method: "DELETE", headers }
      );
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      fetchGroupMembers(selectedGroup.id);
      fetchGroups();
    } catch (error) {
      Swal.fire({ title: "Error!", text: error.message, icon: "error" });
    }
  };

  const fetchAttendance = useCallback(async (groupId) => {
    setIsLoadingAttendance(true);
    try {
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(`${API_BASE}/small-groups/${groupId}/attendance`, { headers });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      const result = await response.json();
      setAttendanceRows(result.data || []);
    } catch (error) {
      console.error("Fetch group attendance failed:", error);
    } finally {
      setIsLoadingAttendance(false);
    }
  }, []);

  const handleAttendance = useCallback(
    (group) => {
      setSelectedGroup(group);
      fetchGroupMembers(group.id);
      fetchAttendance(group.id);
      setAttendanceModalOpen(true);
    },
    [fetchGroupMembers, fetchAttendance]
  );

  const handleCheckInMember = async (memberId) => {
    try {
      const headers = authHeaders();
      if (!headers) return;
      const response = await fetch(`${API_BASE}/attendance`, {
        method: "POST",
        headers: { ...headers, "Content-Type": "application/json" },
        body: JSON.stringify({ member_id: memberId, method: "manual" }),
      });
      if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);
      fetchAttendance(selectedGroup.id);
    } catch (error) {
      Swal.fire({ title: "Error!", text: error.message, icon: "error" });
    }
  };

  const handlePrint = () => window.print();

  return {
    ...groupData,
    handlePrint,
    editModalOpen,
    setEditModalOpen,
    deleteModalOpen,
    setDeleteModalOpen,
    membersModalOpen,
    setMembersModalOpen,
    attendanceModalOpen,
    setAttendanceModalOpen,
    selectedGroup,
    form,
    setForm,
    handleAdd,
    handleSubmit,
    handleDeleteConfirm,
    isSaving,
    leaderSearch,
    setLeaderSearch,
    leaderResults,
    handleSelectLeader,
    groupMembers,
    memberSearch,
    setMemberSearch,
    memberResults,
    isLoadingMembers,
    handleAddGroupMember,
    handleRemoveGroupMember,
    attendanceRows,
    isLoadingAttendance,
    handleCheckInMember,
  };
}
